import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { isResponsiveAttribute } from 'libs/shared/src/models/basic';
import { StyleService } from 'libs/shared/src/services/style/style.service';
import {
  GsFormController,
  GsFormField,
  GsFormFieldBasic,
  GsFormFieldGroup,
  GsFormFieldPadding,
  GsFormIf,
  GsFormIfArgValue,
  GsFormIfOperator,
  GsFormLabeled,
  GsFormSchema,
  GsFormValidation,
  GsFormWithIf,
  GsFormWithValidators
} from 'libs/shared/src/components';
import { schemaDateCompare } from './type-compares/date-compare';
import { schemaDefaultCompare } from './type-compares/default-compare';
import { schemaNumberCompare } from './type-compares/number-compare';
import { schemaStringCompare } from './type-compares/string-compare';

/**
 * Service for handeling tasks associated with the schema and some form tasks.
 */
@Injectable({
  providedIn: 'root'
})
export class SchemaService {
  // Services
  private readonly styleService = inject(StyleService);

  // Properties
  /** RegEx to find a formCtrlName inside arg-Values */
  static readonly FORM_CTRL_NAME_REGEX = /^\{.+\}$/;
  /** RegEx to check if a date string is in ISO 8601 format (RFC 3339) */
  static readonly ISO_RFC_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/i;

  // Methods
  /**
   * Checks if the field should be shown based on the if conditions.
   * @param field to check if it should be shown.
   * @returns True if the field should be shown, false otherwise.
   */
  checkIfArguments(schemaIf: GsFormIf | undefined, formGroup: FormGroup): boolean {
    if (!schemaIf?.args) return true;
    // Default to 'and' if chaining is not specified
    const compareMethod = schemaIf.chaining === 'or' ? 'some' : 'every';
    const res = schemaIf.args[compareMethod]((arg) => {
      const control = formGroup?.get([arg.formControlName]);
      if (!arg.operator) arg.operator = '==';
      // If the arg value is a string and matches the FORM_CTRL_NAME_REGEX, we need to get the value from the form control
      let argValue = arg.value;
      if (typeof arg.value === 'string' && SchemaService.FORM_CTRL_NAME_REGEX.test(arg.value)) {
        const formCtrlName = arg.value.slice(1, -1).replace(' ', ''); // Remove the brackets and spaces
        argValue = formGroup.get([formCtrlName])?.value; // Get the value of the form control
        if (argValue === undefined) {
          console.warn(`[SchemaService] FormControl ${formCtrlName} not found for comparison`);
          return false; // If the form control is not found, return false
        }
      }
      // If the control is found, compare the arg value with the control value. Responsive values are
      // resolved to the current breakpoint first, so conditions on responsive fields (e.g. fillComponentHeight)
      // compare against a scalar instead of the whole ResponsiveAttribute object.
      if (control) {
        const controlValue = isResponsiveAttribute(control.value)
          ? this.styleService.getCurrentResponsiveValue(control.value)
          : control.value;
        return this.compareArg(argValue, controlValue, arg.operator);
      }
      else console.warn(`[SchemaService] FormControl ${arg.formControlName} not found for comparison`);
      return false;
    });
    return res;
  }

  /**
   * Checks if the schema validations are valid based on the formGroup.
   * @param schemaValidations The validations to check.
   * @param formGroup The formGroup to check against.
   * @param isFieldEmpty If the field is empty, it will skip the validation check.
   * @returns True if all validations are valid, false otherwise.
   */
  checkValidations(
    schemaValidations: GsFormValidation | GsFormValidation[] | undefined,
    formGroup: FormGroup,
    isFieldEmpty?: boolean
  ): boolean {
    if (!schemaValidations) return true; // No validations to check
    const validations = Array.isArray(schemaValidations) ? schemaValidations : [schemaValidations];
    if (validations.length === 0 || isFieldEmpty) return true; // No validations to check
    return validations.every((validation) => this.checkIfArguments(validation.if, formGroup));
  }

  /**
   * Converts a form-field of type group to a form-schema.
   * @param formFieldGroup The form-field of type group to convert to a form-schema.
   * @returns The form-schema based on the group.
   */
  toSchema(formFieldGroup: GsFormFieldGroup): GsFormSchema {
    return {
      header: formFieldGroup.label,
      hint: formFieldGroup.description,
      expandable: formFieldGroup.expandable ?? false,
      fields: formFieldGroup.fields ?? []
    };
  }

  // Type Guards
  /** Checks if the field is a GsFormWithIf */
  static isGsFormWithIf(field: GsFormField<unknown>): field is GsFormField<unknown> & GsFormWithIf {
    return 'if' in field;
  }
  /** Checks if the field is a GsFormWithValidators */
  static isGsFormWithValidators(field: GsFormField<unknown>): field is GsFormField<unknown> & GsFormWithValidators {
    return 'validations' in field;
  }
  /** Checks if the field is a GsFormController */
  static isGsFormController<T>(field: unknown): field is GsFormController<T> {
    return typeof field === 'object' && field !== null && 'formControlName' in field && 'type' in field;
  }
  /** Checks if the field is a GsFormFieldBasic */
  static isGsFormFieldBasic<T>(field: unknown): field is GsFormFieldBasic<T> {
    return SchemaService.isGsFormController(field) && SchemaService.isGsFormFieldLabeled(field);
  }
  /** Checks if the field is a GsFormLabeled */
  static isGsFormFieldLabeled(field: unknown): field is GsFormLabeled {
    return typeof field === 'object' && field !== null && 'label' in field;
  }
  /** Checks if the field is a GsFormFieldPadding */
  static isGsFormFieldPadding(field: GsFormField<unknown>): field is GsFormFieldPadding {
    return SchemaService.isGsFormFieldBasic(field) && field.type === 'padding';
  }
  /** Checks if the field is a GsFormFieldResponsive */
  static isGsFormFieldResponsive<T>(field: unknown): field is GsFormFieldBasic<T> & { isResponsive: true } {
    return SchemaService.isGsFormFieldBasic(field) && 'isResponsive' in field && field.isResponsive === true;
  }

  /**
   * Compares an argument value with a value using the specified operator.
   * @param argValue The argument value to compare.
   * @param value The value to compare against.
   * @param operator The operator to use for comparison (e.g., '==', '!=', '<', '>', etc.).
   * @returns True if the comparison is valid, false otherwise.
   */
  private compareArg(argValue: GsFormIfArgValue, value: unknown, operator: GsFormIfOperator): boolean {
    switch (typeof value) {
      case 'string':
        return this.compareArgString(argValue, value, operator);
      case 'number':
        return schemaNumberCompare(argValue, value, operator);
      case 'boolean':
        return schemaDefaultCompare(argValue, value, operator);
      case 'object':
        if (Array.isArray(value)) {
          return value.some((val) => this.compareArg(argValue, val, operator));
        } else {
          if (typeof value === 'object' && value != null) {
            // If the value is an object with a value property, we need to check the value property
            if ('value' in value) return this.compareArg(argValue, value.value, operator);
            // If the value is an object, we can check its type
            if ('constructor' in value) return this.compareArgObject(argValue, value, operator, value.constructor.name);
          }
          return schemaDefaultCompare(argValue, value, operator);
        }
      // An unset control is a valid state, not an unsupported type
      case 'undefined':
        return schemaDefaultCompare(argValue, value, operator);
      default:
        console.debug(`[SchemaService] Unsupported value type for comparison: ${typeof value}`);
        return schemaDefaultCompare(argValue, value, operator);
    }
  }

  /**
   * Compares an argument value with a string value using the specified operator.
   * @param argValue The argument value to compare.
   * @param value The string value to compare against.
   * @param operator The operator to use for comparison (e.g., '==', '!=', '<', '>', etc.).
   * @returns True if the comparison is valid, false otherwise.
   */
  private compareArgString(argValue: GsFormIfArgValue, value: string, operator: GsFormIfOperator): boolean {
    // Check if date string (ISO 8601 format), if so, convert to Date object
    if (value && SchemaService.ISO_RFC_DATE_REGEX.test(value) && !isNaN(Date.parse(value))) {
      const dateValue = new Date(value);
      return schemaDateCompare(argValue, dateValue, operator);
    }
    return schemaStringCompare(argValue, value, operator);
  }

  /**
   * Compares an argument value with a value of a specific object type.
   * @param argValue The argument value to compare.
   * @param value The value to compare against.
   * @param operator The operator to use for comparison (e.g., '==', '!=', '<', '>', etc.).
   * @param objectType The type of the object to compare against (e.g., 'Date', 'Number', 'String').
   * @returns True if the comparison is valid, false otherwise.
   */
  private compareArgObject(argValue: GsFormIfArgValue, value: Object, operator: GsFormIfOperator, objectType?: string | null): boolean {
    if (!objectType) objectType = value.constructor.name;
    switch (objectType) {
      case 'Date':
        return schemaDateCompare(argValue, value as Date, operator);
      case 'Number':
        return schemaNumberCompare(argValue, value as number, operator);
      case 'String':
        return schemaStringCompare(argValue, value as string, operator);
      default:
        console.warn(`[SchemaService] Unsupported object type for comparison: ${objectType}`);
        return schemaDefaultCompare(argValue, value, operator);
    }
  }
}
